import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft, Megaphone, Users, LayoutTemplate, Clock, Send, Wallet, Image as ImageIcon, Video, FileText,
  Sparkles, UploadCloud, ArrowRight, FileSpreadsheet, X, Loader2, Building2, AlertCircle, MessageSquare, ChevronDown,
} from 'lucide-react';
import styles from './CreateCampaign.module.css';
import { useStore } from '../../../../../context/StoreContext';
import { SMIIFY_API } from '../../../../../config/api';

// =============================================================================
// New WhatsApp campaign
//   Branch: to this branch's clients.  HQ: to all (or chosen) branches, paid from the HQ wallet.
//   The cost is worked out live; the money is reserved when you launch and sending runs in the background.
// =============================================================================
const CAMPAIGNS_API = `${SMIIFY_API}/campaigns`;
const TEMPLATE_API = `${SMIIFY_API}/templates`;
const COLUMNS = ['First Name', 'Full Name', 'Last Name', 'Phone Number', 'Salon Name', 'Last Visit Date', 'Wallet Balance', 'Due Amount'];
const MEDIA = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CreateCampaign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeStore, availableStores } = useStore();
  const isHQ = !activeStore || activeStore?.isHQ === true || (activeStore?._id || activeStore?.id) === 'global_hq';
  const storeId = isHQ ? 'global_hq' : String(activeStore?._id || activeStore?.id);
  const branches = useMemo(() => (availableStores || []).filter((s) => !s.isHQ && (s._id || s.id) !== 'global_hq'), [availableStores]);

  const csvRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [tplInfo, setTplInfo] = useState({ line: null, notice: '' });
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [segments, setSegments] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [f, setF] = useState({
    name: '', allBranches: true, branchIds: [], audienceType: 'ALL', segmentId: '', csvFile: null, csvCount: 0,
    filters: { gender: '', lostDays: '', visitedWithinDays: '', hasDue: false, hasWallet: false, birthdayMonth: '', anniversaryMonth: '', minSpent: '' },
    templateId: '', varMapping: {}, scheduleType: 'NOW', scheduledAt: '',
  });
  const [branchCounts, setBranchCounts] = useState({});
  const [showList, setShowList] = useState(false);

  const headers = useCallback((json) => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId, ...(json ? { 'Content-Type': 'application/json' } : {}) }), [storeId]);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const setFilter = (k, v) => setF((x) => ({ ...x, filters: { ...x.filters, [k]: v } }));

  /* ---------------- load templates + segments ---------------- */
  useEffect(() => {
    const segs = JSON.parse(localStorage.getItem('smiify_saved_segments') || '[]');
    if (location.state?.prefilledSegment) { segs.unshift(location.state.prefilledSegment); set({ audienceType: 'SEGMENT', segmentId: location.state.prefilledSegment.id }); }
    setSegments(segs);
    (async () => {
      setLoadingTpl(true);
      try {
        const j = await fetch(TEMPLATE_API, { headers: headers() }).then((r) => r.json());
        if (!j.success) throw new Error(j.message);
        setTemplates((j.data || []).filter((t) => t.status === 'APPROVED'));
        setTplInfo({ line: j.line, notice: j.notice || '' });
      } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setLoadingTpl(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const tpl = templates.find((t) => t.id === f.templateId);
  const body = tpl?.components?.find((c) => c.type === 'BODY')?.text || '';
  const vars = body.match(/\{\{.*?\}\}/g) || [];
  const headerComp = tpl?.components?.find((c) => c.type === 'HEADER');
  const headerFormat = headerComp?.format || 'NONE';
  const sampleMedia = headerComp?.example?.header_handle?.[0] || headerComp?.example?.header_url?.[0] || '';
  const segment = segments.find((s) => s.id === f.segmentId);

  /* the template's own header image, shown in the preview (loaded through the server: Meta blocks direct links) */
  const [mediaPreview, setMediaPreview] = useState('');
  useEffect(() => {
    setMediaPreview('');
    if (headerFormat !== 'IMAGE' || !sampleMedia) return undefined;
    let url = ''; let alive = true;
    fetch(`${CAMPAIGNS_API}/media-proxy?url=${encodeURIComponent(sampleMedia)}`, { headers: headers() })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (alive && b) { url = URL.createObjectURL(b); setMediaPreview(url); } })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [headerFormat, sampleMedia, headers]);

  /* ---------------- live estimate ---------------- */
  const estimateBody = useMemo(() => ({
    scope: isHQ ? 'BRAND' : 'STORE',
    targetStoreIds: isHQ ? JSON.stringify(f.allBranches ? ['ALL'] : f.branchIds) : '[]',
    audienceType: f.audienceType, filters: JSON.stringify(f.filters), userIds: JSON.stringify(segment?.userIds || []),
    csvCount: f.csvCount, category: tpl?.category || 'MARKETING',
  }), [isHQ, f.allBranches, f.branchIds, f.audienceType, f.filters, segment, f.csvCount, tpl]);

  useEffect(() => {
    if (isHQ && !f.allBranches && !f.branchIds.length) { setEstimate(null); return undefined; }
    if (f.audienceType === 'SEGMENT' && !segment) { setEstimate(null); return undefined; }
    const t = setTimeout(async () => {
      setEstimating(true); setEstimateError('');
      try {
        const j = await fetch(`${CAMPAIGNS_API}/estimate`, { method: 'POST', headers: headers(true), body: JSON.stringify(estimateBody) }).then((r) => r.json());
        if (!j.success) throw new Error(j.message);
        setEstimate(j.data);
        if (j.data.byStore?.length) setBranchCounts((x) => ({ ...x, ...Object.fromEntries(j.data.byStore.map((b) => [b.storeId, b.count])) }));
      } catch (e) { setEstimate(null); setEstimateError(e.message); } finally { setEstimating(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [estimateBody, headers, isHQ, f.allBranches, f.branchIds, f.audienceType, segment]);

  /* ---------------- CSV ---------------- */
  const onCsv = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { setError('Upload a .csv file.'); return; }
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const hasHead = /phone|mobile|number|name/i.test(lines[0] || '');
    set({ csvFile: file, csvCount: Math.max(0, lines.length - (hasHead ? 1 : 0)) });
    if (csvRef.current) csvRef.current.value = '';
  };

  /* ---------------- validation ---------------- */
  const allMapped = vars.every((_, i) => f.varMapping[i] && String(f.varMapping[i].value ?? '').trim());
  const mediaOk = !MEDIA.includes(headerFormat) || Boolean(sampleMedia);
  const audienceOk = f.audienceType === 'CSV' ? f.csvFile && f.csvCount > 0 : f.audienceType === 'SEGMENT' ? Boolean(segment) : true;
  const scheduleOk = f.scheduleType === 'NOW' || (f.scheduledAt && new Date(f.scheduledAt) > new Date());
  const enough = estimate ? estimate.enough : false;
  const problems = [
    !f.name.trim() && 'Give the campaign a name.',
    isHQ && !f.allBranches && !f.branchIds.length && 'Choose at least one branch.',
    !audienceOk && 'Choose who gets it.',
    !tpl && 'Choose an approved template.',
    tpl && !allMapped && 'Fill every template variable.',
    tpl && !mediaOk && `This template has no ${headerFormat.toLowerCase()} saved. Add it to the template first.`,
    !scheduleOk && 'Choose a time in the future.',
    estimate && estimate.count === 0 && 'No one matches this audience.',
    estimate && !enough && 'Not enough balance in the wallet.',
  ].filter(Boolean);

  const form = (extra = {}) => {
    const p = new FormData();
    p.append('templateId', f.templateId);
    p.append('varMapping', JSON.stringify(f.varMapping));
    p.append('scope', isHQ ? 'BRAND' : 'STORE');
    Object.entries(extra).forEach(([k, v]) => { if (v instanceof File) p.append(k, v); else if (v !== undefined && v !== null) p.append(k, typeof v === 'string' ? v : JSON.stringify(v)); });
    return p;
  };

  /* ---------------- (no test sends: every message is a real campaign message) ---------------- */
  /* ---------------- launch ---------------- */
  const launch = async () => {
    if (problems.length) return;
    if (!window.confirm(`Send to ${estimate.count.toLocaleString('en-IN')} people? ${inr(estimate.total)} will be reserved from the ${isHQ ? 'HQ' : 'branch'} wallet.`)) return;
    setSubmitting(true); setError('');
    try {
      const p = form({
        name: f.name.trim(), audienceType: f.audienceType, audienceName: f.audienceType === 'SEGMENT' ? segment?.name : f.audienceType === 'CSV' ? f.csvFile?.name : '',
        targetStoreIds: isHQ ? (f.allBranches ? ['ALL'] : f.branchIds) : [], filters: f.filters, userIds: segment?.userIds || [],
        scheduleType: f.scheduleType, scheduledAt: f.scheduleType === 'LATER' ? new Date(f.scheduledAt).toISOString() : undefined,
        ...(f.audienceType === 'CSV' ? { csvFile: f.csvFile } : {}),
      });
      const j = await fetch(`${CAMPAIGNS_API}/launch`, { method: 'POST', headers: headers(), body: p }).then((r) => r.json());
      if (!j.success) throw new Error(j.message);
      navigate(`/marketing/campaigns/${j.campaignId}`, { state: { launched: j.message } });
    } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setSubmitting(false); }
  };

  /* ---------------- preview ---------------- */
  const previewText = () => {
    if (!tpl) return 'Choose a template to see the message...';
    let text = body;
    vars.forEach((v, i) => { const m = f.varMapping[i]; text = text.replace(v, m ? (m.type === 'COLUMN' ? `[${m.value}]` : m.value || '[text]') : `[${v}]`); });
    const html = text.replace(/</g, '&lt;').replace(/\*(.*?)\*/g, '<strong>$1</strong>').replace(/_(.*?)_/g, '<em>$1</em>').replace(/\n/g, '<br/>');
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const lineBadge = tplInfo.line ? `${tplInfo.line.label}${tplInfo.line.templatesManagedBy === 'HIRSTA' ? ' · Hirsta templates' : tplInfo.line.templatesManagedBy === 'HQ' ? ' · HQ templates' : ''}` : '';

  return (
    <div className={styles.pageWrapper}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backBtn} onClick={() => navigate('/marketing/campaigns')} aria-label="Back"><ChevronLeft size={20} strokeWidth={2.5} /></button>
          <h2 className={styles.headerTitle}>{isHQ ? 'New brand campaign (all branches)' : 'New campaign'}</h2>
        </div>
      </header>

      <div className={styles.mainContent}>
        <div className={styles.leftForm}>
          {error && <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: '#fff4f3', color: '#be1025', fontWeight: 700, fontSize: 13 }}><AlertCircle size={15} /> {error}</div>}

          {/* 1. details */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}><Megaphone size={18} className={styles.cardIcon} /><h3 className={styles.cardTitle}>1. Campaign name</h3></div>
            <div className={styles.formGroup}><input className={styles.formInput} placeholder="e.g. Diwali Offer 2026" value={f.name} onChange={(e) => set({ name: e.target.value })} maxLength={80} /></div>
          </div>

          {/* 2. audience */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}><Users size={18} className={styles.cardIcon} /><h3 className={styles.cardTitle}>2. Who gets it</h3></div>

            {isHQ && (
              <div className={styles.formGroup} style={{ marginBottom: 14 }}>
                <label><Building2 size={13} style={{ verticalAlign: '-2px' }} /> Branches</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}><input type="radio" checked={f.allBranches} onChange={() => set({ allBranches: true })} /> All branches ({branches.length}{Object.keys(branchCounts).length ? ` · ${Object.values(branchCounts).reduce((x, y) => x + y, 0).toLocaleString('en-IN')} clients` : ''})</label>
                  <label className={styles.radioLabel}><input type="radio" checked={!f.allBranches} onChange={() => set({ allBranches: false })} /> Choose branches</label>
                </div>
                {!f.allBranches && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {branches.map((b) => {
                      const id = String(b._id || b.id); const on = f.branchIds.includes(id);
                      return <label key={id} className={styles.radioLabel} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${on ? '#673de6' : '#dedee2'}`, background: on ? '#f5f6ff' : '#fff' }}><input type="checkbox" checked={on} onChange={() => set({ branchIds: on ? f.branchIds.filter((x) => x !== id) : [...f.branchIds, id] })} /> {b.storeName || b.name}{branchCounts[id] !== undefined && <b style={{ marginLeft: 4, color: '#471ea7' }}>· {branchCounts[id].toLocaleString('en-IN')}</b>}</label>;
                    })}
                  </div>
                )}
              </div>
            )}

            <div className={styles.audienceTabs}>
              {[['ALL', 'All clients'], ['FILTER', 'Filter'], ['SEGMENT', 'Saved segment'], ['CSV', 'CSV file']].map(([k, label]) => (
                <button key={k} type="button" className={`${styles.tabBtn} ${f.audienceType === k ? styles.active : ''}`} onClick={() => set({ audienceType: k })}>{label}</button>
              ))}
            </div>

            {f.audienceType === 'ALL' && <p style={{ fontSize: 12, color: '#58585e', margin: 0 }}>Every client {isHQ ? 'of the chosen branches' : 'of this branch'} with a valid number. Clients who said STOP are left out.</p>}
            {f.audienceType === 'FILTER' && (
              <div className={styles.formRow} style={{ flexWrap: 'wrap' }}>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Gender</label><select className={styles.formSelect} value={f.filters.gender} onChange={(e) => setFilter('gender', e.target.value)}><option value="">Any</option><option value="Female">Female</option><option value="Male">Male</option></select></div>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Not visited for (days)</label><input className={styles.formInput} type="number" min="0" placeholder="e.g. 60" value={f.filters.lostDays} onChange={(e) => setFilter('lostDays', e.target.value)} /></div>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Visited in last (days)</label><input className={styles.formInput} type="number" min="0" placeholder="e.g. 30" value={f.filters.visitedWithinDays} onChange={(e) => setFilter('visitedWithinDays', e.target.value)} /></div>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Spent at least (₹)</label><input className={styles.formInput} type="number" min="0" placeholder="e.g. 5000" value={f.filters.minSpent} onChange={(e) => setFilter('minSpent', e.target.value)} /></div>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Birthday in</label><select className={styles.formSelect} value={f.filters.birthdayMonth} onChange={(e) => setFilter('birthdayMonth', e.target.value)}><option value="">Any month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></div>
                <div className={styles.formGroup} style={{ flex: '1 1 160px' }}><label>Anniversary in</label><select className={styles.formSelect} value={f.filters.anniversaryMonth} onChange={(e) => setFilter('anniversaryMonth', e.target.value)}><option value="">Any month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></div>
                <div className={styles.formGroup} style={{ flex: '1 1 100%', flexDirection: 'row', gap: 18 }}>
                  <label className={styles.radioLabel}><input type="checkbox" checked={f.filters.hasDue} onChange={(e) => setFilter('hasDue', e.target.checked)} /> Has pending dues</label>
                  <label className={styles.radioLabel}><input type="checkbox" checked={f.filters.hasWallet} onChange={(e) => setFilter('hasWallet', e.target.checked)} /> Has wallet balance</label>
                </div>
              </div>
            )}
            {f.audienceType === 'SEGMENT' && (
              <div className={styles.formGroup}>
                <select className={styles.formSelect} value={f.segmentId} onChange={(e) => set({ segmentId: e.target.value })}>
                  <option value="">Choose a saved segment...</option>
                  {segments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.count} clients)</option>)}
                </select>
                <button type="button" style={{ background: 'none', border: 'none', color: '#673de6', fontWeight: 700, fontSize: 12, marginTop: 10, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} onClick={() => navigate('/marketing/audience')}>Build a segment <ArrowRight size={14} /></button>
              </div>
            )}
            {f.audienceType === 'CSV' && (
              <div className={styles.formGroup}>
                <input type="file" accept=".csv" ref={csvRef} style={{ display: 'none' }} onChange={(e) => onCsv(e.target.files[0])} />
                {!f.csvFile ? (
                  <div className={styles.uploadZone} onClick={() => csvRef.current.click()} role="button" tabIndex={0}><UploadCloud size={32} color="#673de6" /><p>Upload a CSV with a phone column (and name)</p></div>
                ) : (
                  <div className={styles.fileBadge}><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileSpreadsheet size={18} color="#673de6" /><span>{f.csvFile.name} · {f.csvCount} rows</span></div><button type="button" className={styles.removeFile} onClick={() => set({ csvFile: null, csvCount: 0 })} aria-label="Remove"><X size={16} /></button></div>
                )}
              </div>
            )}
          </div>

          {/* 3. template */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}><LayoutTemplate size={18} className={styles.cardIcon} /><h3 className={styles.cardTitle}>3. Message template</h3></div>
            {lineBadge && <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#471ea7', margin: '0 0 10px' }}><MessageSquare size={13} /> {lineBadge}</p>}
            <div className={styles.formGroup}>
              <select className={styles.formSelect} value={f.templateId} onChange={(e) => set({ templateId: e.target.value, varMapping: {} })} disabled={loadingTpl}>
                <option value="">{loadingTpl ? 'Loading templates...' : templates.length ? 'Choose an approved template...' : 'No approved templates on this line yet'}</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({String(t.category).toLowerCase()}, {t.lang})</option>)}
              </select>
              {tpl && <small style={{ marginTop: 6, color: '#58585e', fontSize: 11.5 }}>{tpl.category === 'MARKETING' ? 'Marketing message: charged per message and not sent to clients who said STOP.' : 'Utility message.'}</small>}
            </div>

            {tpl && MEDIA.includes(headerFormat) && (
              <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '10px 0 0', fontSize: 12, fontWeight: 700, color: sampleMedia ? '#471ea7' : '#be1025' }}>
                {headerFormat === 'VIDEO' ? <Video size={13} /> : headerFormat === 'DOCUMENT' ? <FileText size={13} /> : <ImageIcon size={13} />}
                {sampleMedia ? `The ${headerFormat.toLowerCase()} saved in this template is sent with every message.` : `This template has no ${headerFormat.toLowerCase()} saved. Add it to the template first.`}
              </p>
            )}

            {tpl && vars.length > 0 && (
              <div className={styles.mappingBox}>
                <h4 style={{ fontSize: 13, color: '#334155', marginBottom: 12, display: 'flex', alignItems: 'center' }}><Sparkles size={14} style={{ marginRight: 6, color: '#673de6' }} /> Fill the variables</h4>
                {vars.map((v, i) => {
                  const m = f.varMapping[i] || {};
                  return (
                    <div className={styles.mappingRow} key={v + i} style={{ flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', width: '100%', gap: 10, alignItems: 'center' }}>
                        <span className={styles.varBadge} style={{ minWidth: 100, textAlign: 'center' }}>{v}</span>
                        <select className={styles.formSelect} style={{ flex: 1 }} value={m.type === 'CUSTOM' ? 'CUSTOM' : (m.value || '')} onChange={(e) => set({ varMapping: { ...f.varMapping, [i]: e.target.value === 'CUSTOM' ? { type: 'CUSTOM', value: '' } : { type: 'COLUMN', value: e.target.value } } })}>
                          <option value="">Choose...</option>
                          <optgroup label="From each client">{COLUMNS.map((c) => <option key={c} value={c}>{c}</option>)}</optgroup>
                          <optgroup label="Same for everyone"><option value="CUSTOM">Type a text...</option></optgroup>
                        </select>
                      </div>
                      {m.type === 'CUSTOM' && <input className={styles.formInput} style={{ marginTop: 8, marginLeft: 110, width: 'calc(100% - 110px)' }} placeholder="e.g. 20% off this week" value={m.value} onChange={(e) => set({ varMapping: { ...f.varMapping, [i]: { type: 'CUSTOM', value: e.target.value } } })} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 4. schedule */}
          <div className={styles.formCard}>
            <div className={styles.cardHeader}><Clock size={18} className={styles.cardIcon} /><h3 className={styles.cardTitle}>4. When</h3></div>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}><input type="radio" checked={f.scheduleType === 'NOW'} onChange={() => set({ scheduleType: 'NOW' })} /> Send now</label>
              <label className={styles.radioLabel}><input type="radio" checked={f.scheduleType === 'LATER'} onChange={() => set({ scheduleType: 'LATER' })} /> Schedule</label>
            </div>
            {f.scheduleType === 'LATER' && <div className={styles.formGroup} style={{ marginTop: 12, maxWidth: 280 }}><input type="datetime-local" className={styles.formInput} value={f.scheduledAt} onChange={(e) => set({ scheduledAt: e.target.value })} /></div>}
          </div>
        </div>

        {/* ---------------- right ---------------- */}
        <div className={styles.rightPreview}>
          <div className={styles.summaryBox}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: '#18181a', marginBottom: 14 }}><Wallet size={18} color="#673de6" /> Cost {estimating && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}</h3>
            {estimateError ? <p style={{ color: '#be1025', fontSize: 12.5, fontWeight: 700 }}>{estimateError}</p> : !estimate ? <p style={{ color: '#797980', fontSize: 12.5 }}>Choose the audience to see the cost.</p> : (
              <>
                <div className={styles.summaryRow}><span style={{ color: '#797980' }}>People</span><span style={{ fontWeight: 800 }}>{estimate.count.toLocaleString('en-IN')}{isHQ ? ` in ${estimate.stores} branches` : ''}</span></div>
                {estimate.skipped && (estimate.skipped.invalid + estimate.skipped.duplicate + estimate.skipped.optedOut) > 0 && (
                  <div className={styles.summaryRow}><span style={{ color: '#797980' }}>Left out</span><span style={{ fontSize: 12 }}>{[estimate.skipped.optedOut && `${estimate.skipped.optedOut} said STOP`, estimate.skipped.duplicate && `${estimate.skipped.duplicate} repeated`, estimate.skipped.invalid && `${estimate.skipped.invalid} bad numbers`].filter(Boolean).join(', ')}</span></div>
                )}
                {isHQ && estimate.byStore?.filter((b) => b.selected).length > 0 && (
                  <div style={{ margin: '4px 0 8px', padding: '8px 10px', borderRadius: 8, background: '#f5f6ff', fontSize: 12 }}>
                    {estimate.byStore.filter((b) => b.selected).map((b) => <div key={b.storeId} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>{b.storeName}</span><b>{b.count.toLocaleString('en-IN')}</b></div>)}
                  </div>
                )}
                {estimate.preview?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <button type="button" onClick={() => setShowList((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: '#673de6', fontWeight: 800, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                      <ChevronDown size={13} style={{ transform: showList ? 'rotate(180deg)' : 'none' }} /> See who gets it{estimate.count > estimate.preview.length ? ` (first ${estimate.preview.length})` : ''}
                    </button>
                    {showList && (
                      <div style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid #e6e8ff', borderRadius: 8 }}>
                        {estimate.preview.map((p) => (
                          <div key={p.phone} style={{ display: 'flex', gap: 8, padding: '6px 10px', borderBottom: '1px dashed #e6e8ff', fontSize: 12 }}>
                            <span style={{ flex: 1, fontWeight: 700 }}>{p.name || 'Client'}</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>+{p.phone}</span>
                            {isHQ && <span style={{ color: '#797980', minWidth: 80, textAlign: 'right' }}>{p.storeName}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.summaryRow}><span style={{ color: '#797980' }}>Per message</span><span style={{ fontWeight: 800 }}>{estimate.perMessage ? inr(estimate.perMessage) : 'Free'}</span></div>
                <div className={styles.summaryRow} style={{ fontSize: 15 }}><span style={{ fontWeight: 800 }}>Total</span><span style={{ fontWeight: 900, color: '#673de6' }}>{inr(estimate.total)}</span></div>
                <div className={styles.summaryRow}><span style={{ color: '#797980' }}>{isHQ ? 'HQ' : 'Branch'} wallet available</span><span style={{ fontWeight: 800, color: enough ? '#007a49' : '#be1025' }}>{inr(estimate.wallet?.available)}</span></div>
                {!enough && (
                  <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: '#fff4f3', color: '#be1025', fontSize: 12.5, fontWeight: 800 }}>
                    Need {inr(estimate.total - (estimate.wallet?.available || 0))} more.{' '}
                    <button type="button" onClick={() => navigate('/wallet-billing')} style={{ border: 'none', background: 'none', color: '#673de6', fontWeight: 900, cursor: 'pointer', padding: 0 }}>Recharge</button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.waPreviewMockup} style={{ marginTop: 16 }}>
            <div className={styles.waBubble}>
              {MEDIA.includes(headerFormat) && (
                <div className={styles.waHeaderImage} style={{ backgroundColor: '#e2e8f0', display: 'flex', justifyContent: 'center', alignItems: 'center', height: 140, overflow: 'hidden' }}>
                  {mediaPreview ? <img src={mediaPreview} alt="Template image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : headerFormat === 'VIDEO' ? <Video size={32} color="#94a3b8" /> : headerFormat === 'DOCUMENT' ? <FileText size={32} color="#94a3b8" /> : <ImageIcon size={32} color="#94a3b8" />}
                </div>
              )}
              <div className={styles.waTextContent}>{previewText()}</div>
              {tpl?.components?.find((c) => c.type === 'FOOTER')?.text && <div className={styles.waFooterPreview}>{tpl.components.find((c) => c.type === 'FOOTER').text}</div>}
              <div className={styles.waTime}>10:42 AM</div>
            </div>
          </div>
        </div>
      </div>

      <footer className={styles.bottomBar}>
        <div style={{ color: '#be1025', fontSize: 13, fontWeight: 600 }}>{problems[0] || ''}</div>
        <button type="button" className={styles.btnPrimary} disabled={problems.length > 0 || submitting || estimating || !estimate} onClick={launch}>
          {submitting ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Starting...</> : <><Send size={16} /> {f.scheduleType === 'LATER' ? 'Schedule' : 'Send'}{estimate ? ` to ${estimate.count.toLocaleString('en-IN')}` : ''}</>}
        </button>
      </footer>
    </div>
  );
};

export default CreateCampaign;